/**
 * Builds the URL used to fetch data for the Home component on componentDidMount(). Depending on
 * the value of the parameter 'source' it will map to the either /plink or /twlink paths.
 * @param  {String} locationSearch String containing the value of location.search in the component
 * @return {String}                The URL to call the API
 */
export const homeFetchUrl = locationSearch => {
  let fetchUrl = process.env.REACT_APP_API_HOST;
  fetchUrl += fetchUrl && fetchUrl.endsWith("/") ? "" : "/";
  if (locationSearch) {
    const searchParams = new URLSearchParams(locationSearch);
    if (searchParams.get("source") === "plink") {
      searchParams.delete("source");
      fetchUrl = `${fetchUrl}plink?${searchParams.toString()}`;
    }
    if (searchParams.get("source") === "twlink") {
      searchParams.delete("source");
      fetchUrl = `${fetchUrl}twlink?${searchParams.toString()}`;
    }
  }
  return fetchUrl;
};

export const modelFetchUrl = (locationSearch, geo) => {
  let fetchUrl = process.env.REACT_APP_API_HOST;
  fetchUrl += fetchUrl && fetchUrl.endsWith("/") ? "" : "/";
  fetchUrl += `${geo}/scores`;
  if (locationSearch) {
    const searchParams = new URLSearchParams(locationSearch);
    if (searchParams.get("source") === "plink") {
      searchParams.delete("source");
      fetchUrl = `${fetchUrl}plink?${searchParams.toString()}`;
    }
    if (searchParams.get("source") === "twlink") {
      searchParams.delete("source");
      fetchUrl = `${fetchUrl}twlink?${searchParams.toString()}`;
    }
  }
  return fetchUrl;
};

/**
 * Builds the URL used to fetch the model scores for the Home component on the callback function
 * handleChangeCallback() called by the modelcontrols prop in the Chart component.
 * @param  {Number} id         The id of the model to retrieve the scores for
 * @param  {String} startDate  Start date of requested time period. In ISO format YYYY-MM-DD
 * @param  {String} endDate    End date of requested time period. In ISO format YYYY-MM-DD
 * @param  {Number} resolution The density of the data points returned, either day or week
 * @param  {String} smoothing  Number of days to smooth data over using a moving average filter
 * @return {String}            The URL to call the API
 */
export const homeFetchScoresUrl = (
  id,
  startDate,
  endDate,
  resolution,
  smoothing
) => {
  let fetchUrl = process.env.REACT_APP_API_HOST;
  fetchUrl += fetchUrl && fetchUrl.endsWith("/") ? "" : "/";
  const searchParams = new URLSearchParams();
  searchParams.set("id", id);
  searchParams.set("startDate", startDate);
  searchParams.set("endDate", endDate);
  searchParams.set("resolution", resolution);
  searchParams.set("smoothing", smoothing);
  return `${fetchUrl}scores?${searchParams.toString()}`;
};

/**
 * Generates the URL used to fetch the scores based on the parameters configured in the
 * DataFiltering component.
 * @param  {Array} ids         Array containing the ids of the models to be present in the URL
 * @param  {String} startDate  Start date of requested time period. In ISO format YYYY-MM-DD
 * @param  {String} endDate    End date of requested time period. In ISO format YYYY-MM-DD
 * @param  {String} resolution The density of the data points returned, either day or week
 * @param  {Number} smoothing  Number of days to smooth data over using a moving average filter
 * @return {String}            The URL to be used in the permaLink
 */
export const dataFilteringQueryUrl = (
  ids,
  startDate,
  endDate,
  resolution,
  smoothing
) => {
  let fetchUrl = process.env.REACT_APP_API_HOST;
  fetchUrl += fetchUrl && fetchUrl.endsWith("/") ? "" : "/";
  const searchParams = new URLSearchParams();
  ids.forEach(id => {
    searchParams.append("id", id);
  });
  searchParams.set("startDate", startDate);
  searchParams.set("endDate", endDate);
  searchParams.set("resolution", resolution);
  searchParams.set("smoothing", smoothing);
  const endpointUrl = `${fetchUrl}scores`;
  return `${endpointUrl}?${searchParams.toString()}`;
};

/**
 * Generates the URL used to download the scores in CSV format
 * @param  {Object} modelList The list of active models
 * @param  {String} startDate Start date of requested time period. In ISO format YYYY-MM-DD
 * @param  {String} endDate   End date of requested time period. In ISO format YYYY-MM-DD
 * @param  {String} resolution Either week or day
 * @return {String}           The URL to download the CSV file from
 */
export const rawScoresCsvUrl = (modelList, startDate, endDate, resolution) => {
  const modelids = modelList
    .map(m => m.id)
    .map(m => `id=${m}`)
    .join("&");
  let apihost = `${process.env.REACT_APP_API_HOST}`;
  apihost += apihost.endsWith("/") ? "" : "/";
  return `${apihost}csv?${modelids}&startDate=${startDate}&endDate=${endDate}&resolution=${resolution}&ctype=.csv`;
};

export const downloadCsvUrl = (geo) => {
  let apihost = `${process.env.REACT_APP_API_HOST}`;
  apihost += apihost.endsWith("/") ? "" : "/";
  apihost += `${geo}/`
  return `${apihost}csv`;
};

export const permalinkUrl = (
  ids,
  startDate,
  endDate,
  resolution,
  smoothing
) => {
  const searchParams = new URLSearchParams();
  searchParams.set("source", "plink");
  ids.forEach(id => {
    searchParams.append("id", id);
  });
  searchParams.set("startDate", startDate);
  searchParams.set("endDate", endDate);
  searchParams.set("resolution", resolution);
  searchParams.set("smoothing", smoothing);
  const pUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
  return `${pUrl}?${searchParams.toString()}`;
};

export const queryNamesUrl = () => {
  let apihost = `${process.env.REACT_APP_API_HOST}`;
  apihost += apihost.endsWith("/") ? "" : "/";
  return `${apihost}queries`;
};
